import { CheckCircle2 } from "lucide-react";

export interface InBoxItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  qty: string;
  note?: string;
}

interface Rem7HydraInBoxGridProps {
  shortName: string;
  items: InBoxItem[];
  /** Optional eyebrow / caption above the grid. */
  caption?: string;
}

/**
 * Premium "What's in the Box" grid for REM7 Hydra products.
 * Black-gold styled item cards with quantity, ideal for the
 * REM7 Hydra Whirlpool (M1825) and its variants.
 */
export const Rem7HydraInBoxGrid = ({
  shortName,
  items,
  caption,
}: Rem7HydraInBoxGridProps) => (
  <section
    id="in-the-box"
    className="border-b border-neutral-200 bg-white py-20"
  >
    <div className="container mx-auto px-6 lg:px-12">
      <div className="mx-auto max-w-6xl rounded-2xl border border-neutral-200 bg-neutral-900 p-8 text-white shadow-lg lg:p-12">
        <div className="flex flex-col gap-3 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">
              What's in the Box
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold sm:text-3xl">
              Included with every {shortName}
            </h2>
            <div className="mt-3 h-px w-12 bg-amber-400" />
          </div>
          <p className="text-xs text-neutral-400 sm:max-w-sm sm:text-right">
            {caption ??
              "A complete EU-ready spa kit. Every component is factory-fitted or supplied loose, ready for standard plumbing rough-in."}
          </p>
        </div>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <li
              key={item.label}
              className="flex items-start gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-amber-500/40 hover:bg-white/[0.06]"
            >
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md border border-amber-500/30 bg-amber-500/10">
                <item.icon className="h-5 w-5 text-amber-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300/80">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="font-serif text-sm font-semibold leading-snug text-white">
                    {item.label}
                  </p>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
                  {item.qty}
                </p>
                {item.note && (
                  <p className="mt-1 text-[11px] leading-snug text-neutral-400">
                    {item.note}
                  </p>
                )}
              </div>
              <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-amber-300" />
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-6 text-[11px] uppercase tracking-[0.18em] text-neutral-400">
          <span>Total Items · {items.length}</span>
          <span className="text-amber-300/70">·</span>
          <span>Standard EU Plumbing Rough-in</span>
          <span className="text-amber-300/70">·</span>
          <span>CE / cUPC Certified</span>
        </div>
      </div>
    </div>
  </section>
);
